import { Link, Outlet } from "react-router-dom";
import { TabWithOutlet } from "../../components/tab";
import { useEffect, useState } from "react";
import { getSnowflakeJobSettings } from "@/helpers/apis";
import { useQuery } from "@tanstack/react-query";
import { useAppState } from "@/modules/app/useAppContext";
import { isOwner } from "@/services/auth";
import { Button, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";

const WORKLOAD_TAB_INDEX = 2;
const DBT_TAB_INDEX = 3;
const STREAMLIT_TAB_INDEX = 4;
const NOTEBOOKS_TAB_INDEX = 5;
const STORED_PROCEDURES_TAB_INDEX = 6;
const TASKS_TAB_INDEX = 7;
const TABLEAU_TAB_INDEX = 8;
const AI_SERVICES_TAB_INDEX = 9;

const QueryContainer = () => {
  const {
    dbtWorkloadEnabled,
    enableBiFeature,
    streamlitNotebooksEnabled,
    storedProceduresEnabled,
    snowflakeTasksEnabled,
    enableAIServicesInCodePage,
    queryAiServicesOwnerOnly,
    userGroup,
  } = useAppState();
  const [tabs, setTabs] = useState([
    { disabled: false, label: "All Queries", value: "all_queries", path: "/query" },
    { disabled: false, label: "Groups", value: "groups", path: "/query/groups" },
    { disabled: true, label: "Workload", value: "workload", path: "/query/snowflake-jobs" }, // WORKLOAD_TAB_INDEX
    { disabled: true, label: "dbt", value: "dbt", path: "/query/dbt-jobs" }, // DBT_TAB_INDEX
    { disabled: true, label: "Streamlit", value: "streamlit", path: "/query/streamlit" }, // STREAMLIT_TAB_INDEX
    { disabled: true, label: "Notebooks", value: "notebooks", path: "/query/notebooks" }, // NOTEBOOKS_TAB_INDEX
    {
      disabled: true,
      label: "Stored Procedures",
      value: "stored_procedures",
      path: "/query/stored-procedures",
      betaMode: true,
    }, // STORED_PROCEDURES_TAB_INDEX
    {
      disabled: true,
      label: "Tasks",
      value: "tasks",
      path: "/query/tasks",
      betaMode: true,
    }, // TASKS_TAB_INDEX
    { disabled: true, label: "Tableau", path: "/query/tableau-jobs" }, // TABLEAU_TAB_INDEX
    {
      disabled: true,
      label: "AI Services",
      value: "ai_services",
      path: "/query/ai-services",
      betaMode: true,
    }, // AI_SERVICES_TAB_INDEX
  ]);

  const { data } = useQuery({
    queryKey: ["snowflakeJobSettings"],
    queryFn: getSnowflakeJobSettings,
    gcTime: 5 * 60 * 1000,
  });

  const enableTab = (index: number) => {
    setTabs(prev => {
      const newTabs = [...prev];
      newTabs[index].disabled = false;
      return newTabs;
    });
  };

  const maybeEnableWorkloadTab = () => {
    if (data?.workflow_id) {
      enableTab(WORKLOAD_TAB_INDEX);
    }
  };

  const maybeEnableDbtWorkloadTab = () => {
    if (dbtWorkloadEnabled) {
      enableTab(DBT_TAB_INDEX);
    }
  };

  const maybeEnableStreamlitTab = () => {
    if (streamlitNotebooksEnabled) {
      enableTab(STREAMLIT_TAB_INDEX);
    }
  };

  const maybeEnableNotebooksTab = () => {
    if (streamlitNotebooksEnabled) {
      enableTab(NOTEBOOKS_TAB_INDEX);
    }
  };

  const maybeEnableStoredProceduresTab = () => {
    if (storedProceduresEnabled) {
      enableTab(STORED_PROCEDURES_TAB_INDEX);
    }
  };

  const maybeEnableTasksTab = () => {
    if (snowflakeTasksEnabled) {
      enableTab(TASKS_TAB_INDEX);
    }
  };

  const maybeEnableTableauTab = () => {
    if (enableBiFeature) {
      enableTab(TABLEAU_TAB_INDEX);
    }
  };

  const maybeEnableAiServicesTab = () => {
    // When queryAiServicesOwnerOnly is set, the tab is restricted to tenant
    // Owners acting at the organization level (hidden in team scope, even for
    // Owners).
    const ownerAtOrgScope = isOwner() && !userGroup;
    if (enableAIServicesInCodePage && (!queryAiServicesOwnerOnly || ownerAtOrgScope)) {
      enableTab(AI_SERVICES_TAB_INDEX);
    }
  };

  useEffect(() => {
    // First time when this useEffect is getting called
    // data is coming as undefined. Without this the ordering
    // is not correct.
    if (!data) {
      return;
    }
    maybeEnableWorkloadTab();
    maybeEnableDbtWorkloadTab();
    maybeEnableStreamlitTab();
    maybeEnableNotebooksTab();
    maybeEnableStoredProceduresTab();
    maybeEnableTasksTab();
    maybeEnableTableauTab();
    maybeEnableAiServicesTab();
  }, [data]);

  return (
    <div>
      <Stack justify={"between"} align={"center"} className="al-mb-4">
        <Typography variant="page-heading">Code</Typography>
        {data?.workflow_id ? (
          <Button asChild>
            <Link to={"/settings/snowflake-job-settings"}>Workload settings</Link>
          </Button>
        ) : null}
      </Stack>
      <TabWithOutlet tabs={tabs} />
      <Outlet />
    </div>
  );
};

export { QueryContainer };
